<template lang="pug">
v-container.pa-8.neutral200.mb-10(fluid)
  v-form(ref="form")
    .mb-4
      .d-flex.align-center.mb-4
        v-btn.mr-4(icon small)
          v-icon(color="primary" @click="onClickBack") mdi-arrow-left
        .text-2xl.font-semibold.neutral700--text {{ id ? $t('edit_template') : $t('create_new_template') }}
      .text-lg.font-medium.neutral700--text {{ id ? $t('edit_template_description') : $t('new_template_description') }}
      .text-sm.font-normal.neutral700--text {{ id ? $t('edit_template_subdescription') : $t('new_template_subdescription') }}
    template(v-if="isFetching")
      v-skeleton-loader.px-4(type="list-item-three-line@5")
    template(v-else)
      v-row.mb-4(no-gutters)
        v-col
          v-card.white.pa-6.border-outline.round-2xl.mb-4(outlined)
            .text-md.font-semibold.neutral700--text.mb-4 {{ $t('template_setup') }}
            v-row.mb-4(no-gutters)
              v-col.mr-4
                .text-sm.font-semibold.neutral700--text.mb-1 {{ $t('template_name') }}
                v-text-field.text-sm.round-xl(v-model="name" outlined :placeholder="$t('template_name_placeholder')" hide-details="auto" dense :rules="rules.not_empty")
              v-col
                .text-sm.font-semibold.neutral700--text.mb-1 {{ $t('template_description') }}
                v-text-field.text-sm.round-xl(v-model="description" outlined :placeholder="$t('template_description_placeholder')" hide-details="auto" dense)
            .text-sm.font-semibold.neutral700--text.mb-1 {{ $t('template_category') }}
            v-radio-group.ma-0(v-model="category" mandatory hide-details="auto")
              v-card.pa-4(v-for="category in categoryOptions" :key="category.id" outlined :ripple="false" rounded="lg" :class="[category !== 3 ? 'mb-3' : '', {'border-selected':isSelected(category)} ]" @click="onSelected(category)")
                v-radio(:value="category" readonly)
                  template(v-slot:label)
                    .d-flex.flex-column(:class="isSelected(category) ? 'neutral700--text' : 'neutral500--text'")
                      .text-sm.font-semibold {{ category.title }}
                      .text-sm.font-normal {{ category.description }}
          document-template-header(v-if="category.value !== 'patient_label'" :headerData="headerData")
          v-card.white.pa-6.border-outline.round-2xl.mb-4(v-if="hasComposeContent.includes(category.value)" outlined)
            .text-md.font-semibold.neutral700--text(:class="['medical_document', 'patient_label'].includes(category.value) ? 'mb-4' : ''") {{ $t('compose_content') }}
            template(v-if="category.value === 'medical_document'")
              text-editor.mb-4(:parentForm.sync="content" :category="category.value")
            template(v-if="category.value === 'invoice'")
              .text-sm.font-normal.neutral500--text.mb-4 {{ $t('compose_content_description_item', { item: $t('invoice').toLowerCase() }) }}
              template(v-for="invoice in invoiceContentItems")
                y-checkbox.mb-1(v-model="invoice.value")
                  template(v-slot:label)
                    .text-md.ml-1 {{ invoice.text }}
            template(v-if="category.value === 'receipt'")
              .text-sm.font-normal.neutral500--text.mb-4 {{ $t('compose_content_description_item', { item: $t('receipt').toLowerCase() }) }}
              template(v-for="receipt in receiptContentItems")
                y-checkbox.mb-1(v-model="receipt.value")
                  template(v-slot:label)
                    .text-md.ml-1 {{ receipt.text }}
            template(v-if="category.value === 'patient_label'")
              text-editor.mb-4(:parentForm.sync="content" :category="category.value")
          v-card.white.pa-6.border-outline.round-2xl.mb-4(v-if="category.value !== 'patient_label'" outlined)
            v-row(no-gutters)
              v-col.pr-15
                .text-md.font-semibold.neutral700--text {{ $t('footer') }}
                .text-sm.font-normal.mb-4 {{ $t('footer_description') }}
              v-col.shrink
                y-switch(v-model="footerEnabled")
            template(v-if="footerImage")
              v-card.d-flex.pa-2.round-xl.mb-4.align-center.hover(outlined @mouseover="isHoverImage = true" @mouseout="isHoverImage = false")
                .flex-grow-0.mr-4
                  v-img(width="40" height="40" :src="footerImage.url ? footerImage.url : footerImage.file" contain)
                .text-sm.flex-grow-1
                  .font-medium.truncate {{ footerImage.data ? footerImage.data.name : footerImage.path }}
                  .neutral500--text(v-if="footerImage.data") {{ footerImage.data.size | bytesToSize }} - 100% {{ $t('uploaded') }}
                v-icon.ml-4(v-if="isHoverImage" size="22" color="neutral700" @click="openDeleteConfirmationDialog = true") $yzi-trash-03
                v-icon.ml-4(v-else size="22" color="success") $yzi-check-circle-solid
            template(v-else)
              v-card.round-xl.border-dashed.pa-8.mb-4(outlined)
                .text-center
                  v-avatar.mb-3(color="neutral300" size="40")
                    v-icon(color="neutral500" size="18") $yzi-image-01
                  .mb-1.text-sm
                    span.info500--text.font-semibold {{ $t('click_to_upload') }} 
                    span.neutral700--text {{ $t('or_drag_and_drop') }}
                  .text-xs.neutral500--text {{ $t('photo_upload_limitation') }}
                input.input-change-layout(ref="file" type="file" @change="onFileChange" accept="image/*")
            template(v-if="footerImage")
              .text-md.font-semibold.neutral700--text.mb-1 {{ $t('alignment') }}
              v-btn-toggle.round-xl.mb-4.pr-3(v-model="footerAlignment" mandatory style="width: 100%")
                v-btn.flex-grow-1.white
                  v-icon.mr-1(:color="getToggleIconClass(0)" size="20") mdi-format-align-left
                  .text-none.font-semibold.text-sm(:class="getToggleBtnClass(0)") {{ $t('align_item', { item: $t('left') }) }}
                v-btn.flex-grow-1.white
                  v-icon.mr-1(:color="getToggleIconClass(1)" size="20") mdi-format-align-center
                  .text-none.font-semibold.text-sm(:class="getToggleBtnClass(1)") {{ $t('align_item', { item: $t('center') }) }}
                v-btn.flex-grow-1.white
                  v-icon.mr-1(:color="getToggleIconClass(2)" size="20") mdi-format-align-right
                  .text-none.font-semibold.text-sm(:class="getToggleBtnClass(2)") {{ $t('align_item', { item: $t('right') }) }}
          v-card.white.pa-6.border-outline.round-2xl(v-if="hasPricing" outlined)
            v-row(no-gutters)
              v-col.pr-15
                .text-md.font-semibold.neutral700--text Pricing
                .text-sm.neutral700--text Set default pricing tier for this item. You can manage your pricing tier in settings.
                y-button(label="Go to pricing tier setting" size="xs" color="transparent" text-color="neutral700" icon-color="neutral700" icon-size="15" underline button-class="pa-0" append-icon="mdi-arrow-top-right" @click="goToPriceTiers")
              v-col.shrink
                y-switch(v-model="pricingTierEnabled")
            v-data-table.mt-2(:headers="priceTierHeaders" :items="pricingTiers" item-key="id" :items-per-page="-1" :hide-default-header="pricingTiers.length === 0" hide-default-footer :loading="isFetchingPricingTiers" disable-sort :header-props="{ sortIcon: '$yzi-triangle' }")
              template(#no-data)
                div.empty-box.mt-6.pa-8(align="center")
                  v-avatar(color="neutral300" size="54")
                    v-icon(color="neutral500" size="30") mdi-tag-outline
                  div.text-center.text-md.font-semibold.neutral700--text.mt-6 You don’t have any pricing tier yet
                  div.text-center.text-sm.font-normal.neutral500--text.mt-1 Set your pricing tier now
                  y-button.mt-6(label="Set your pricing tier" size="sm" color="neutral700" text-color="neutral100" icon-color="neutral100" icon-size="20" append-icon="mdi-arrow-top-right" @click="goToPriceTiers")
              template(v-slot:item.name="{ item }")
                div.py-2
                  .text-sm.font-semibold.neutral700--text {{ item.name }}
                  .text-sm.neutral700--text {{ item.description }}
              template(v-slot:item.payment="{ item }")
                div.d-flex.align-center.py-2
                  div
                    .text-xs.neutral700--text Panel: {{ getPanelCount(item.panels) }}
                    .text-xs.neutral700--text.mt-1 Self-Pay: {{ getSelfPayCount(item.self_pay) }}
                  v-btn.ml-3(icon small @click="openDetails(item)")
                    v-icon(size="20") mdi-information-outline
              template(v-slot:item.price="{ item }")
                div(style="width: 150px;")
                  v-text-field.text-sm.my-2.round-xl(v-model="item.price" placeholder="0.00" outlined dense height="48" :prefix="$currency.symbol" :rules="pricingTierEnabled ? rules.not_empty : []" type="number" hide-details="auto")
        v-col.pl-8
          //- .sticky
          preview
            preview-item.mb-12(:title="$t('template_preview')")
              document-template-preview.mt-4(:header="headerData" :category="category.value" :content="content" :receiptData="receiptContentItems" :invoiceData="invoiceContentItems" :footerUrl="footerUrl" :footerAlignment="footerAlignment")
  v-footer.white(v-if="!isFetching" padless fixed style="z-index: 20;")
    .d-flex.flex-column.grow.pb-2
      v-divider.mb-2
      .d-flex.justify-end.align-center.mr-4
        v-btn.button-md.underline.mr-2(text @click="onClickBack") {{ $t('cancel') }}
        v-btn.button-md(depressed :loading="isLoading" color="primary" @click="onSave") {{ $t('save') }}
  .dialogs
    pricing-tier-details-dialog(v-if="openPricingTierDetailsDialog" :open.sync="openPricingTierDetailsDialog" :tier="selectedTier" @closed="selectedTier = null")
    delete-footer-image-dialog(v-if="openDeleteConfirmationDialog" :open.sync="openDeleteConfirmationDialog" :parentId="id" @deleted="onFooterImageDeleted")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { resizeImage } from '@/utils'
import TextEditor from '@/components/clinic/document/TextEditor.vue'
import Preview from '@/components/clinic/settings/Preview.vue'
import PreviewItem from '@/components/clinic/settings/PreviewItem.vue'
import PricingTierDetailsDialog from '@/components/clinic/inventory/PricingTierDetailsDialog.vue'
import DocumentTemplateHeader from '@/components/clinic/document/DocumentTemplateHeader.vue'
import DeleteFooterImageDialog from '@/components/clinic/dialogs/DeleteFooterImageDialog.vue'
import DocumentTemplatePreview from '@/components/clinic/document/preview/DocumentTemplatePreview.vue'

const MEDICAL_DOCUMENT = 'medical_document'
const MEDICAL_CERTIFICATE = 'medical_certificate'
const INVOICE = 'invoice'
const RECEIPT = 'receipt'
const PRESCRIPTION_LETTER = 'prescription_letter'
const PATIENT_LABEL = 'patient_label'

export default {
  components: {
    TextEditor, Preview, PreviewItem, PricingTierDetailsDialog, DocumentTemplateHeader, DeleteFooterImageDialog, DocumentTemplatePreview
  },
  data() {
    return {
      isFetching: false,
      name: null,
      description: null,
      content: null,
      category: { 
        id: 1,
        title: this.$t('medical_records'),
        description: this.$t('medical_records_description'),
        value: MEDICAL_DOCUMENT
      },
      footerEnabled: false,
      footerAlignment: 1,
      footerImage: null,
      categoryOptions: [
        { 
          id: 1,
          title: this.$t('medical_records'),
          description: this.$t('medical_records_description'),
          value: MEDICAL_DOCUMENT
        },
        { 
          id: 2,
          title: this.$t('medical_certificate'),
          description: this.$t('medical_certificate_description'),
          value: MEDICAL_CERTIFICATE
        },
        {
          id: 3,
          title: this.$t('invoice'),
          description: this.$t('invoice_description'),
          value: INVOICE
        },
        {
          id: 4,
          title: this.$t('receipt'),
          description: this.$t('receipt_description'),
          value: RECEIPT
        },
        { 
          id: 5,
          title: 'Prescription Letter',
          description: 'Template for prescription letter to the patient',
          value: PRESCRIPTION_LETTER
        },
        { 
          id: 6,
          title: 'Patient Label',
          description: 'Template for patient label',
          value: PATIENT_LABEL
        },
      ],
      rules: {
        not_empty: [v => (v != null && v.toString().trim().length) || 'This field is required'],
      },
      isFetchingPricingTiers: false,
      pricingTiers: [],
      pricingTierEnabled: true,
      priceTierHeaders: [
        { text: 'Name', value: 'name', enable: true, class: 'text-table-header' },
        { text: 'Payment methods', value: 'payment', enable: true, class: 'text-table-header' },
        { text: 'Price', value: 'price', enable: true, class: 'text-table-header' },
      ],
      invoiceContentItems: [
        {
          text: 'Recipient info',
          value: true
        },
        {
          text: 'Invoice number',
          value: true
        },
        {
          text: 'Invoice date',
          value: true
        },
        {
          text: 'Invoice info',
          value: true
        },
        {
          text: 'Digital invoice note',
          value: true
        },
        {
          text: 'Summarize info',
          value: true
        },
      ],
      receiptContentItems: [
        {
          text: 'Recipient info', 
          value: true
        },
        {
          text: 'Receipt number', 
          value: true
        },
        {
          text: 'Receipt date', 
          value: true
        },
        {
          text: 'Payment declaration', 
          value: true
        },
        {
          text: 'Receipt info', 
          value: true
        },
        {
          text: 'Digital receipt note', 
          value: true
        },
        {
          text: 'Summarize info', 
          value: true
        },
      ],
      selectedTier: null,
      openPricingTierDetailsDialog: false,
      isLoading: false,
      headerData: {
        name: null,
        phone: null,
        address: {},
        resizeImageFileUrl: '',
        resizeImageFile: null,
      },
      openDeleteConfirmationDialog: false,
      isHoverImage: false,
      hasComposeContent: [MEDICAL_DOCUMENT, INVOICE, RECEIPT, PATIENT_LABEL]
    }
  },
  async mounted () {
    this.isFetching = true
    try {
      if (this.id) {
        await this.mapData()
      } else {
        await this.fetchPricingTiers()
      }
      this.isFetching = false
    } catch (error) {
      this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    id () {
      return this.$route.params?.id ?? null
    },
    emptyAddress() {
      if (Object.keys(this.address).length === 0) {
        return true
      } else if (Object.values(this.address).every(val => !val)) {
        return true
      }
      return false
    },
    payload () {
      let payload = {
        company: this.company.id,
        name: this.name,
        description: this.description,
        category: this.category.value,
        footer_enabled: this.footerEnabled,
        footer_alignment: this.getFooterAlignmentPayload(),
      }
      switch (this.category.value) {
        case MEDICAL_DOCUMENT:
          payload.content = this.content
          payload.price_tier_enabled = this.pricingTierEnabled
          if (this.pricingTierEnabled) {
            payload.price_tiers = this.pricingTiers.map(m => { return { price_tier: m.id, price: Math.round(m.price * this.$currency.multiplier) } })
          }
          break

        case MEDICAL_CERTIFICATE:
          payload.price_tier_enabled = this.pricingTierEnabled
          if (this.pricingTierEnabled) {
            payload.price_tiers = this.pricingTiers.map(m => { return { price_tier: m.id, price: Math.round(m.price * this.$currency.multiplier) } })
          }
          break

        case INVOICE:
          payload.properties = {
            recipient_info: this.invoiceContentItems[0].value,
            invoice_number: this.invoiceContentItems[1].value,
            invoice_date: this.invoiceContentItems[2].value,
            invoice_info: this.invoiceContentItems[3].value,
            digital_invoice_note: this.invoiceContentItems[4].value,
            summarize_info: this.invoiceContentItems[5].value
          }
          break

        case RECEIPT:
          payload.properties = {
            recipient_info: this.receiptContentItems[0].value,
            receipt_number: this.receiptContentItems[1].value,
            receipt_date: this.receiptContentItems[2].value,
            payment_declaration: this.receiptContentItems[3].value,
            receipt_info: this.receiptContentItems[4].value,
            digital_receipt_note: this.receiptContentItems[5].value,
            summarize_info: this.receiptContentItems[6].value
          }
          break

        case PATIENT_LABEL:
          payload.content = this.content
          delete payload.footer_enabled
          delete payload.footer_alignment
          break
      
        default:
          break
      }
      return payload
    },
    params () {
      return {
        company: this.company.id,
        status: 'active',
        page_size: -1
      }
    },
    footerUrl () {
      if (!this.footerEnabled) {
        return null
      }
      if (!this.footerImage) {
        return null
      }
      return this.footerImage.url || this.footerImage.file
    },
    hasPricing () {
      const hasPricingCategories = [MEDICAL_DOCUMENT, MEDICAL_CERTIFICATE]
      if (hasPricingCategories.includes(this.category.value)) {
        return true
      }
      return false
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    onClickBack () {
      this.$router.push('/admin/clinic/settings/document/templates')
    },
    onSelected (category) {
      this.category = category
    },
    isSelected (category) {
      return this.category.id === category.id
    },
    goToPriceTiers () {
      this.$router.push('/admin/clinic/settings/price-tier-settings')
    },
    getToggleBtnClass (index) {
      return this.footerAlignment === index ? 'neutral700--text' : 'neutral500--text'
    },
    getToggleIconClass (index) {
      return this.footerAlignment === index ? 'neutral700' : 'neutral500'
    },
    getFooterAlignmentPayload () {
      switch (this.footerAlignment) {
        case 0:
          return 'left'

        case 1:
          return 'middle'

        case 2:
          return 'right'
      
        default:
          return
      }
    },
    fetchPricingTiers (pricetiers) {
      this.isFetchingPricingTiers = true
      return this.$axios.get('product_pricetiers', { params: this.params })
        .then(resp => {
          if (resp.data && resp.data.results) {
            if (pricetiers) {
              let data = []
              resp.data.results.forEach(o => {
                let ptier = pricetiers.find(f => f.price_tier.id === o.id)
                o.price = null
                if (ptier) {
                  o.price = ptier.price / this.$currency.multiplier
                }
                data.push(o)
              })
              this.pricingTiers = data
            } else {
              this.pricingTiers = resp.data.results.map(m => { return { ...m, price: null } })
            }
          }
        })
        .finally(() => {
          this.isFetchingPricingTiers = false
        })
    },
    openDetails (item) {
      this.selectedTier = item
      this.openPricingTierDetailsDialog = true
    },
    getPanelCount (panels) {
      const includedPanel = panels.length
      if (this.pricingTiers.total_panels === includedPanel) {
        return 'All included'
      } else if (includedPanel === 0) {
        return 'None included'
      } else {
        return includedPanel + ' included'
      }
    },
    getSelfPayCount (methods) {
      const includedMethods = methods.length
      if (includedMethods === this.pricingTiers.total_self_pay) {
        return 'All included'
      } else if (includedMethods === 0) {
        return 'None included'
      } else {
        return includedMethods + ' included'
      }
    },
    async onSave () {
      if (!this.$refs.form.validate()) {
        this.showMessage(['error', this.$t('error_message.please_fill_out_all_required')])
        return
      }
      try {
        this.isLoading = true
        if (this.id) {
          await this.$axios.patch(`/document_templates/${this.id}`, this.payload)
          if (this.footerImage?.data) {
            await this.uploadFooterImage(this.id)
          }
        } else {
          const { data } = await this.$axios.post('/document_templates', this.payload)
          if (this.footerImage?.data) {
            await this.uploadFooterImage(data.id)
          }
        }
        this.onClickBack()
        this.showMessage(['success', this.$t('success_message.update_successful')])
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isLoading = false
      }
    },
    async onFileChange(e) {
      const files = e.target.files || e.dataTransfer.files
      if (!files.length) {
        return
      }
      const resizedImage = await resizeImage(files[0], `photo_${Date.now()}.jpeg`)
      const photo = {
        url: resizedImage.dataURL,
        data: resizedImage.file,
      }
      this.footerImage = photo
      this.footerEnabled = true
    },
    uploadFooterImage (id) {
      const params = new FormData()
      params.append('footer_image', this.footerImage.data)
      return this.$axios.patch(`/document_templates/${id}`, params, { headers: { 'Content-Type': 'multipart/form-data' }})
    },
    async mapData () {
      return this.$axios.get(`/document_templates/${this.id}`)
        .then(async({ data }) => {
          const { name, description, category, footer_enabled, footer_image, footer_alignment, price_tiers, properties, content, price_tier_enabled } = data
          this.name = name
          this.description = description
          switch (category) {
            case MEDICAL_DOCUMENT:
              this.category = this.categoryOptions[0]
              break

            case MEDICAL_CERTIFICATE:
              this.category = this.categoryOptions[1]
              break

            case INVOICE:
              this.category = this.categoryOptions[2]
              break

            case RECEIPT:
              this.category = this.categoryOptions[3]
              break

            case PRESCRIPTION_LETTER:
              this.category = this.categoryOptions[4]
              break
            
            case PATIENT_LABEL:
              this.category = this.categoryOptions[5]
              break
          
            default:
              break
          }
          this.footerEnabled = footer_enabled
          this.footerImage = footer_image
          if (footer_alignment) {
            this.footerAlignment = ['left', 'middle', 'right'].findIndex(item => item === footer_alignment)
          }
          this.pricingTierEnabled = price_tier_enabled
          if (price_tiers.length > 0) {
            await this.fetchPricingTiers(price_tiers)
          } else {
            await this.fetchPricingTiers()
          }
          switch (this.category.value) {
            case MEDICAL_DOCUMENT:
              this.content = content
              break

            case INVOICE:
              this.invoiceContentItems[0].value = properties.recipient_info
              this.invoiceContentItems[1].value = properties.invoice_number
              this.invoiceContentItems[2].value = properties.invoice_date
              this.invoiceContentItems[3].value = properties.invoice_info
              this.invoiceContentItems[4].value = properties.digital_invoice_note
              this.invoiceContentItems[5].value = properties.summarize_info
              break

            case RECEIPT:
              this.receiptContentItems[0].value = properties.recipient_info
              this.receiptContentItems[1].value = properties.receipt_number
              this.receiptContentItems[2].value = properties.receipt_date
              this.receiptContentItems[3].value = properties.payment_declaration
              this.receiptContentItems[4].value = properties.receipt_info
              this.receiptContentItems[5].value = properties.digital_receipt_note
              this.receiptContentItems[6].value = properties.summarize_info
              break

            case PATIENT_LABEL:
              this.content = content
              break
          
            default:
              break
          }
        })
    },
    onFooterImageDeleted () {
      this.footerImage = null
      this.footerEnabled = false
      this.$emit('changed')
    },
    onAddSmartField (field) {
      this.$refs.editor.insert(field)
    }
  },
}
</script>

<style lang="scss" scoped>
.border-outline {
  border: 1px solid #EEEEEE !important;
}
::v-deep {
  .v-text-field--outlined {
    fieldset {
      border-color: var(--v-neutral400);
    }
  }
  .error--text {
    color: var(--v-danger500) !important;
    fieldset {
      border-color: var(--v-danger500);
    }
  }
  .combostyle .v-input__icon {
    min-width: 22px;
    width: 22px;
    margin-top: 4px;
  }
}
::v-deep .v-ripple__container {
  display:none !important;
}
.field-outline {
  border: 1px solid var(--v-neutral400) !important;
}
.input-change-layout {
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  opacity: 0;
}
.hover:hover {
  background-color: rgba(0, 0, 0, 0.04) !important;
}
.sticky {
  top: 6vh;
  position: sticky;
}
</style>